import React, { useEffect, useState } from 'react';
import { Transaction, Store, Product, VerificationLog } from '../types';
import { X, Package, Scale, Store as StoreIcon, CreditCard, User, Calendar, ShieldAlert, XCircle, PackagePlus, Tag } from 'lucide-react';
import { getApiBase } from '../utils/apiBase';

interface TransactionModalProps {
  transaction: Transaction;
  stores: Store[];
  products: Product[];
  onClose: () => void;
}

interface PromotionSnapshot {
  promotionId?: string;
  name: string;
  discountType: 'percentage' | 'flat' | 'bundle';
  discountValue: number;
  bundleQty?: number | null;
  bundlePrice?: number | null;
  savings: number;
}

const primary = '#F1561D';
const API_BASE = getApiBase();

const TransactionModal: React.FC<TransactionModalProps> = ({ transaction, stores, products, onClose }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [verificationLogs, setVerificationLogs] = useState<VerificationLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const itemsPerPage = 5;
  const totalItems = transaction.items.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const isFlagged = String(transaction.status) === 'flagged_for_review';
  // Being flagged for review is an overlay on top of a payment that already
  // succeeded, not a status of its own — the badge should always read the real
  // outcome (Completed), with "Needs Review" shown alongside as a separate note.
  const displayStatus = isFlagged ? 'completed' : transaction.status;
  const totalSavings = (transaction as any).totalSavings as number | undefined;
  const hasAnySavings = totalSavings && totalSavings > 0;

  const getStoreName = (storeId: any) => {
    if (typeof storeId === 'object' && storeId !== null) {
      if (storeId.name) return storeId.name;
      const s = stores.find(s => s._id === storeId._id);
      return s ? s.name : 'Unknown';
    }
    if (typeof storeId === 'string') {
      const s = stores.find(s => s._id === storeId);
      return s ? s.name : 'Unknown';
    }
    return 'Unknown';
  };

  const getProductName = (item: any) => {
    if (item.name) return item.name;
    if (item.isWeighed) return 'Weighed Item';
    if (item.productId) {
      const p = products.find(p => p._id === item.productId);
      if (p) return p.name;
    }
    return item.productName || 'Unknown';
  };

  const normalizeImageUrl = (url?: string | null) => {
    if (!url) return '';
    return url.startsWith('http://') ? url.replace(/^http:\/\//i, 'https://') : url;
  };

  // Fetch verification logs for flagged transactions
  useEffect(() => {
    if (!isFlagged) return;
    setLogsLoading(true);
    const token = localStorage.getItem('authToken');
    fetch(`${API_BASE}/api/transactions/verification-log?transactionReference=${transaction.reference}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(json => setVerificationLogs(json.data || []))
      .catch(() => setVerificationLogs([]))
      .finally(() => setLogsLoading(false));
  }, [transaction.reference, isFlagged]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => { setCurrentPage(1); }, [transaction]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const visibleItems = transaction.items.slice(startIndex, startIndex + itemsPerPage);

  // Two distinct discrepancy kinds: a barcode that was paid for but never
  // scanned back (missing), and a barcode scanned more times than it was
  // paid for (over-scanned / extra).
  const missingLogs = verificationLogs.filter(l => l.type === 'discrepancy');
  const overScannedLogs = verificationLogs.filter(l => l.type === 'over_scanned');
  const hasAnyFlaggedLogs = missingLogs.length > 0 || overScannedLogs.length > 0;

  // Keyed off displayStatus — 'flagged_for_review' is never looked up here anymore
  // since it's rendered as the separate "Needs Review" note instead.
  const statusColor: Record<string, [string, string]> = {
    completed:  ['#065f46', '#d1fae5'],
    pending:    ['#92400e', '#fef3c7'],
    failed:     ['#991b1b', '#fee2e2'],
    reversed:   ['#1e40af', '#dbeafe'],
    refunded:   ['#6b21a8', '#f3e8ff'],
  };
  const [sc, sbg] = statusColor[displayStatus] ?? ['#374151', '#f3f4f6'];

  const metaItem = (icon: React.ReactNode, label: string, value: string) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '14px 0', borderBottom: '1px solid #f9fafb' }}>
      <div style={{ color: '#d1d5db', marginTop: '1px', flexShrink: 0 }}>{icon}</div>
      <div>
        <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</p>
        <p style={{ margin: '3px 0 0', fontSize: '14px', color: '#111827', fontWeight: 500 }}>{value}</p>
      </div>
    </div>
  );

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 50, padding: '16px', fontFamily: "'DM Sans', sans-serif" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      <div style={{ background: '#fff', borderRadius: '20px', width: '100%', maxWidth: '640px', maxHeight: '90vh', display: 'grid', gridTemplateRows: 'auto 1fr auto', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.25)' }}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}>Transaction Details</h2>
            <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#9ca3af', fontFamily: 'monospace' }}>{transaction.reference}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Status pill always reads the real payment outcome */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, color: sc, background: sbg }}>
              {displayStatus.replace(/_/g, ' ')}
            </span>
            {/* Needs Review side-note — shown alongside, not instead of, the real status */}
            {isFlagged && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 600, color: '#9a3412', background: '#ffedd5' }}>
                <ShieldAlert size={12} /> Needs Review
              </span>
            )}
            <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: '8px', padding: '7px', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#6b7280' }}>
              <X size={16} />
            </button>
          </div>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────────── */}
        <div style={{ overflowY: 'auto', padding: '24px' }}>

          {/* ── Flagged alert banner ──────────────────────────────────── */}
          {isFlagged && (
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '12px', padding: '16px', marginBottom: '24px', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              <ShieldAlert size={20} color="#ea580c" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <p style={{ margin: 0, fontSize: '13px', fontWeight: 600, color: '#9a3412' }}>Payment Completed — Needs Review</p>
                <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#c2410c' }}>
                  This transaction completed successfully, but a discrepancy was detected during the exit
                  security check — either an item paid for wasn't scanned back, or an item was scanned more
                  times than it was paid for. Review the details below.
                </p>
              </div>
            </div>
          )}

          {/* ── Total banner ──────────────────────────────────────────── */}
          <div style={{ background: 'linear-gradient(135deg, #F1561D, #ff8a5b)', borderRadius: '14px', padding: '20px 24px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.75)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total Amount</p>
              <p style={{ margin: '4px 0 0', fontSize: '36px', fontWeight: 600, color: '#fff', lineHeight: 1, fontFamily: 'monospace' }}>
                R {transaction.total.toFixed(2)}
              </p>
              {hasAnySavings && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '8px', background: 'rgba(255,255,255,0.2)', borderRadius: '20px', padding: '4px 10px' }}>
                  <Tag size={11} color="rgba(255,255,255,0.9)" />
                  <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.95)', fontWeight: 600 }}>
                    Saved R {(totalSavings as number).toFixed(2)} with promotions
                  </p>
                </div>
              )}
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.75)' }}>
                {transaction.items.length} item{transaction.items.length !== 1 ? 's' : ''}
              </p>
              <p style={{ margin: '4px 0 0', fontSize: '14px', color: '#fff', fontWeight: 500, textTransform: 'capitalize' }}>
                {transaction.paymentMethod}
              </p>
            </div>
          </div>

          {/* ── Meta grid ────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px', marginBottom: '24px' }}>
            <div>
              {metaItem(<StoreIcon size={15} />, 'Store', getStoreName(transaction.storeId))}
              {metaItem(<User size={15} />, 'Customer', transaction.customer.name)}
            </div>
            <div>
              {metaItem(<Calendar size={15} />, 'Date', new Date(transaction.createdAt).toLocaleDateString())}
              {metaItem(<CreditCard size={15} />, 'Payment', transaction.paymentMethod || 'N/A')}
            </div>
          </div>

          {/* ── Customer photo ────────────────────────────────────────── */}
          {(transaction.customer as any)?.photo && (
            <div style={{ marginBottom: '24px' }}>
              <p style={{ margin: '0 0 8px', fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customer Photo</p>
              <img
                src={normalizeImageUrl((transaction.customer as any).photo)}
                alt="Customer"
                style={{ maxHeight: '100px', maxWidth: '100%', objectFit: 'contain', borderRadius: '10px', border: '1px solid #f3f4f6' }}
                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            </div>
          )}

          {/* ── Flagged items (missing + over-scanned) ───────────────── */}
          {isFlagged && (
            <div style={{ marginBottom: '24px' }}>
              <p style={{ margin: '0 0 10px', fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Flagged Items
              </p>
              {logsLoading ? (
                <p style={{ fontSize: '13px', color: '#9ca3af' }}>Loading…</p>
              ) : !hasAnyFlaggedLogs ? (
                <p style={{ fontSize: '13px', color: '#9ca3af' }}>No flagged item details found.</p>
              ) : (
                <>
                  {/* Missing items — paid for but never scanned back */}
                  {missingLogs.map(log => (
                    <div key={log._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', marginBottom: '8px' }}>
                      <XCircle size={18} color="#dc2626" style={{ flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                          {log.productName && log.productName !== 'Unknown item' ? log.productName : `Barcode: ${log.barcode ?? '—'}`}
                        </p>
                        {log.barcode && (
                          <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{log.barcode}</p>
                        )}
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#dc2626', background: '#fee2e2', padding: '3px 8px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                        Not in cart
                      </span>
                    </div>
                  ))}

                  {/* Over-scanned items — every paid unit already matched, this scan is extra */}
                  {overScannedLogs.map(log => (
                    <div key={log._id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 14px', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', marginBottom: '8px' }}>
                      <PackagePlus size={18} color="#c2410c" style={{ flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <p style={{ margin: 0, fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                          {log.productName && log.productName !== 'Unknown item' ? log.productName : `Barcode: ${log.barcode ?? '—'}`}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                          {log.barcode && (
                            <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{log.barcode}</p>
                          )}
                          {log.expectedQuantity != null && (
                            <p style={{ margin: 0, fontSize: '11px', color: '#9ca3af' }}>
                              · Paid for {log.expectedQuantity}, scanned {log.scannedQuantity ?? '—'}
                            </p>
                          )}
                        </div>
                      </div>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#c2410c', background: '#ffedd5', padding: '3px 8px', borderRadius: '20px', whiteSpace: 'nowrap' }}>
                        Extra scan
                      </span>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* ── Items table ───────────────────────────────────────────── */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <p style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: '#374151' }}>Items Purchased</p>
              {totalItems > itemsPerPage && (
                <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                  {startIndex + 1}–{Math.min(startIndex + itemsPerPage, totalItems)} of {totalItems}
                </span>
              )}
            </div>

            <div style={{ border: '1px solid #f3f4f6', borderRadius: '12px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#fafafa' }}>
                    {['Product', 'Qty', 'Unit Price', 'Subtotal'].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleItems.map((item, idx) => {
                    const promos = (item as any).promotions as PromotionSnapshot[] | undefined;
                    const hasPromos = promos && promos.length > 0;
                    const finalPrice = (item as any).finalPrice as number | undefined;
                    const originalSubtotal = item.qty * item.price;
                    // finalPrice from backend is the discounted line total
                    const actualSubtotal = finalPrice != null ? finalPrice : originalSubtotal;
                    const isDiscounted = hasPromos && actualSubtotal < originalSubtotal;

                    return (
                      <tr key={`${item.productId || idx}-${startIndex + idx}`} style={{ borderTop: '1px solid #f9fafb' }}>

                        {/* Product name + badges */}
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: item.isWeighed ? '#fff5f2' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' }}>
                              {item.isWeighed ? <Scale size={14} color={primary} /> : <Package size={14} color="#9ca3af" />}
                            </div>
                            <div>
                              <div style={{ fontSize: '14px', color: '#111827', fontWeight: 500, lineHeight: '1.3' }}>
                                {getProductName(item)}
                                {item.isWeighed && (
                                  <span style={{ marginLeft: '6px', background: '#fff5f2', color: '#ea580c', fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: '20px' }}>
                                    Weighed
                                  </span>
                                )}
                              </div>
                              {/* Promotion tags */}
                              {hasPromos && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '5px' }}>
                                  {promos!.map((promo, pi) => (
                                    <span
                                      key={pi}
                                      style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', background: '#f0fdf4', color: '#16a34a', fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', border: '1px solid #bbf7d0' }}
                                    >
                                      <Tag size={9} />
                                      {promo.name}
                                      {promo.savings > 0 && (
                                        <span style={{ color: '#15803d' }}> −R{promo.savings.toFixed(2)}</span>
                                      )}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Qty */}
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151' }}>
                          {item.qty}
                        </td>

                        {/* Unit Price */}
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#374151' }}>
                          R {item.price.toFixed(2)}
                        </td>

                        {/* Subtotal — strikethrough if discounted */}
                        <td style={{ padding: '12px 16px' }}>
                          {isDiscounted ? (
                            <div>
                              <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af', textDecoration: 'line-through' }}>
                                R {originalSubtotal.toFixed(2)}
                              </p>
                              <p style={{ margin: '2px 0 0', fontSize: '14px', fontWeight: 600, color: '#16a34a' }}>
                                R {actualSubtotal.toFixed(2)}
                              </p>
                            </div>
                          ) : (
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#111827' }}>
                              R {originalSubtotal.toFixed(2)}
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Totals summary row ────────────────────────────────── */}
            {hasAnySavings && (
              <div style={{ marginTop: '12px', padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Tag size={13} color="#16a34a" />
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#15803d' }}>Total Savings</span>
                </div>
                <span style={{ fontSize: '14px', fontWeight: 700, color: '#15803d' }}>
                  −R {(totalSavings as number).toFixed(2)}
                </span>
              </div>
            )}

            {/* ── Pagination ────────────────────────────────────────── */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                  style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '13px', color: currentPage === 1 ? '#d1d5db' : '#374151', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                >
                  ← Prev
                </button>
                <div style={{ display: 'flex', gap: '6px' }}>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      style={{ width: '30px', height: '30px', borderRadius: '50%', border: 'none', fontSize: '13px', cursor: 'pointer', background: currentPage === p ? primary : 'transparent', color: currentPage === p ? '#fff' : '#374151', fontWeight: currentPage === p ? 600 : 400 }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                  style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '13px', color: currentPage === totalPages ? '#d1d5db' : '#374151', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f3f4f6', background: '#fafafa', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ background: primary, color: '#fff', border: 'none', borderRadius: '10px', padding: '10px 32px', fontSize: '14px', fontWeight: 500, cursor: 'pointer' }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionModal;
